import { usePhotoCreate, useRecordingCreate, useVideoCreate } from '@/hooks';
import type { StagedItem } from '@/store/useCaptureFlowStore';
import type { CaptureEdgeKind } from '@vivport/backend/types';
import type { ObservationGroupInput } from '@/api/content-api';

export interface StagedSubmitResult {
  photoIds: number[];
  recordingIds: number[];
  videoIds: number[];
  edgeKind: CaptureEdgeKind | null;
  observationGroupId: number | null;
  failedItemIds: string[];
  error: unknown | null;
}

interface UseObservationCaptureSubmitResult {
  submitStaged: (
    items: readonly StagedItem[],
    linkToContentId: number | null,
    observationGroup: ObservationGroupInput,
    originProvenanceContentId?: number
  ) => Promise<StagedSubmitResult>;
}

// linkToContentId and observationGroup are independent and both may apply to one
// submit: linkToContentId files evidence under business content (the server picks
// attachment vs link from the graph matrix — this never calls the attachment or link
// endpoints itself), while observationGroup records co-capture provenance (which
// items were captured together). Clearing one never touches the other.
export function useObservationCaptureSubmit(): UseObservationCaptureSubmitResult {
  const { createRecording } = useRecordingCreate();
  const { createPhotos } = usePhotoCreate();
  const { createVideo } = useVideoCreate();

  // One session is one observation whatever the member's submit rhythm: the walk is
  // strictly sequential so `{ start: true }` can be reached at most once — the first
  // create's group id rewrites `group` and every later create joins it. Submits run one
  // at a time on the session's queue, so that holds across submits too.
  const submitStaged = async (
    items: readonly StagedItem[],
    linkToContentId: number | null,
    observationGroup: ObservationGroupInput,
    originProvenanceContentId?: number
  ): Promise<StagedSubmitResult> => {
    const photos = items.filter((item) => item.kind === 'photo');
    const recordings = items.filter((item) => item.kind === 'recording');
    const videos = items.filter((item) => item.kind === 'video');
    const photoIds: number[] = [];
    const recordingIds: number[] = [];
    const videoIds: number[] = [];
    const unsent = new Set(items.map((item) => item.id));
    let group = observationGroup;
    let edgeKind: CaptureEdgeKind | null = null;
    let observationGroupId: number | null = null;

    try {
      if (photos.length > 0) {
        const created = await createPhotos(
          photos.map((item) => item.file),
          { linkToContentId, observationGroup: group, originProvenanceContentId }
        );
        photoIds.push(...created.items.map((item) => item.id));
        edgeKind = created.items.find((item) => item.edgeKind !== null)?.edgeKind ?? null;
        if (created.observationGroupId !== null) {
          observationGroupId = created.observationGroupId;
          group = { groupId: created.observationGroupId };
        }
        // Only the prefix that actually reached the server is retired; createPhotos stops
        // at its first failure and returns creations in input order.
        photos.slice(0, created.items.length).forEach((item) => unsent.delete(item.id));
        if (created.error !== null) throw created.error;
      }

      for (const item of recordings) {
        const created = await createRecording(item.file, {
          linkToContentId,
          observationGroup: group,
          originProvenanceContentId,
        });
        recordingIds.push(created.id);
        edgeKind = edgeKind ?? created.edgeKind;
        if (created.observationGroupId !== null) {
          observationGroupId = created.observationGroupId;
          group = { groupId: created.observationGroupId };
        }
        unsent.delete(item.id);
      }

      for (const item of videos) {
        const created = await createVideo(item.file, {
          linkToContentId,
          observationGroup: group,
          originProvenanceContentId,
          ...(item.durationSeconds !== undefined ? { durationSeconds: item.durationSeconds } : {}),
        });
        videoIds.push(created.id);
        edgeKind = edgeKind ?? created.edgeKind;
        if (created.observationGroupId !== null) {
          observationGroupId = created.observationGroupId;
          group = { groupId: created.observationGroupId };
        }
        unsent.delete(item.id);
      }

      return { photoIds, recordingIds, videoIds, edgeKind, observationGroupId, failedItemIds: [], error: null };
    } catch (error) {
      // Whatever reached the server stays gone from the tray; the rest stays staged so
      // the member can retry it, and the retry joins the group the walk already started.
      return {
        photoIds,
        recordingIds,
        videoIds,
        edgeKind,
        observationGroupId,
        failedItemIds: [...unsent],
        error,
      };
    }
  };

  return { submitStaged };
}
